import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setDropdownOpen(false);
    }
  }, []);

  useEffect(() => {
    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpen, handleClickOutside]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200/70"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-16">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 no-underline">
              <span className="text-lg md:text-xl font-heading font-bold text-foreground-900 whitespace-nowrap">
                Vigorbuy<span className="text-primary-500">Spread</span>
              </span>
            </a>

            <Link
              to="/"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <i className="ri-home-line"></i>
              <span>Home</span>
            </Link>

            <Link
              to="/article/vigorbuy-spreadsheet-buying-tips"
              className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-foreground-600 text-sm font-medium hover:bg-background-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <i className="ri-book-open-line"></i>
              <span>Shopping Guide</span>
            </Link>
          </div>

          <div className="hidden md:flex items-center gap-2">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-grid-line"></i>
                <span>Browse Categories</span>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 border border-background-200/70 rounded-xl shadow-lg overflow-hidden z-50 max-h-[420px] overflow-y-auto">
                  <div className="p-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-primary-50 transition-colors cursor-pointer group no-underline"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 group-hover:bg-primary-200/70 transition-colors flex-shrink-0">
                          <i className={`${cat.icon} text-base`}></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <i className="ri-shopping-cart-line"></i>
              <span>Vigorbuy Spreadsheet</span>
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-5 py-2 rounded-lg bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <i className="ri-flashlight-line"></i>
              <span>Buy It Now</span>
            </a>
          </div>

          <div className="flex md:hidden items-center gap-2">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
            >
              <span>Vigorbuy Spreadsheet</span>
            </a>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 border border-background-200/70 text-foreground-700 cursor-pointer"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <i className={`text-lg ${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 top-16 bg-background-50 z-40 overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors no-underline"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 flex-shrink-0">
                  <i className="ri-home-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Home</span>
              </Link>

              <Link
                to="/article/vigorbuy-spreadsheet-buying-tips"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-background-100 transition-colors no-underline"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-100/70 text-accent-600 flex-shrink-0">
                  <i className="ri-book-open-line text-lg"></i>
                </div>
                <span className="text-sm font-semibold text-foreground-900">Shopping Guide</span>
              </Link>
            </div>

            <div className="border-t border-background-200/70 pt-4">
              <h3 className="px-4 text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-2">Product Categories</h3>
              <div className="grid grid-cols-2 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl hover:bg-primary-50 transition-colors cursor-pointer no-underline"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/70 text-primary-600 flex-shrink-0">
                      <i className={`${cat.icon} text-sm`}></i>
                    </div>
                    <span className="text-xs font-medium text-foreground-800 leading-tight">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-4 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
              >
                <i className="ri-shopping-cart-line"></i>
                <span>Vigorbuy Spreadsheet</span>
              </a>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 rounded-xl bg-accent-500 text-background-50 text-sm font-semibold hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
              >
                <i className="ri-flashlight-line"></i>
                <span>Buy It Now</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}